import { cloneDeep } from 'es-toolkit/compat';
import { createContext, Dispatch } from 'react';

import { initialQueryStrings } from './initialQueryStrings';
import { initialScrollPositions } from './initialScrollPositions';

export type QueryStringKey =
  | 'adminAdvancePayor'
  | 'adminAdvances'
  | 'adminBulkRemove'
  | 'adminCompanyDetails'
  | 'adminCreditExposure'
  | 'adminCreditLimit'
  | 'adminCsrDashboard'
  | 'adminCsrInvoices'
  | 'adminCsrLoads'
  | 'adminDashboard'
  | 'adminFactorAssignment'
  | 'adminFactorAssignmentAudit'
  | 'adminFactorAssignmentHistory'
  | 'adminInvoicePayDateChangeReport'
  | 'adminInvoices'
  | 'adminMisdirectedPayments'
  | 'adminNoaActionConflictsHistoryList'
  | 'adminNoaActionConflictsList'
  | 'adminNoaCoachableNoasList'
  | 'adminNoaList'
  | 'adminNoaPendingFactorsList'
  | 'adminNoaSearchList'
  | 'adminNoaSplitList'
  | 'adminPayorList'
  | 'adminPendingCarrierBanks'
  | 'adminPreviousBankChanges'
  | 'adminPromotionalCampaignRelationships'
  | 'adminPromotionalCampaigns'
  | 'adminSelectCarrierClaimedRelationships'
  | 'adminSelectCarrierPotentialRelationships'
  | 'adminSelectCarriers'
  | 'adminTriumphLoadAdvancePayor'
  | 'adminTriumphLoadAdvances'
  | 'adminUnreconciledFundsPayors'
  | 'adminUnreconciledFundsPayorShow'
  | 'factorAllInvoices'
  | 'factorAllLoads'
  | 'factorInvoices'
  | 'factorLoads'
  | 'factorPayees'
  | 'factorPayments'
  | 'factorPayors'
  | 'metricAggregates'
  | 'metricHistory'
  | 'noaProcessorNoaList'
  | 'payeeInvoices'
  | 'payeePaperwork'
  | 'payeePayments'
  | 'payorBillingReport'
  | 'payorCreditLimitReport'
  | 'payorCreditNotes'
  | 'payorGotpFeesReport'
  | 'payorHeldInvoices'
  | 'payorHighwayAlerts'
  | 'payorInvoiceRefundsReport'
  | 'payorInvoiceReview'
  | 'payorInvoices'
  | 'payorLoads'
  | 'payorPaperwork'
  | 'payorPayments'
  | 'payorPreviousSettlementReport'
  | 'payorPreviousWeeklySettlementReport'
  | 'payorRelationships'
  | 'payorRevenueShareReport'
  | 'payorUnreconciledFunds';

type QueryStrings = Record<QueryStringKey, string>;
type ScrollPositions = Record<QueryStringKey, number>;

export interface NavigationState {
  currentPath: { path: string };
  isScrollPositionEnabled: boolean;
  queryStrings: QueryStrings;
  scrollPositions: ScrollPositions;
}

interface ResetAction {
  type: 'reset';
}
interface SetPathAction {
  payload: { path: string };
  type: 'setPath';
}
interface SetQueryStringsAction {
  payload: {
    queryStrings: Partial<NavigationState['queryStrings']>;
  };
  type: 'setQueryStrings';
}
interface SetScrollPositionAction {
  payload: {
    scrollPositionKey: QueryStringKey;
    scrollPositionValue: number;
  };
  type: 'setScrollPosition';
}
interface SetIsScrollPositionEnabled {
  payload: { isScrollPositionEnabled: boolean };
  type: 'setIsScrollPositionEnabled';
}

export type NavigationContextAction =
  | ResetAction
  | SetIsScrollPositionEnabled
  | SetPathAction
  | SetQueryStringsAction
  | SetScrollPositionAction;

type NavigationDispatch = Dispatch<NavigationContextAction>;

export const NavigationStateContext = createContext<NavigationState>({
  currentPath: {
    path: '',
  },
  isScrollPositionEnabled: false,
  queryStrings: cloneDeep(initialQueryStrings),
  scrollPositions: cloneDeep(initialScrollPositions),
});
export const NavigationDispatchContext = createContext<NavigationDispatch>(undefined as unknown as NavigationDispatch);

NavigationStateContext.displayName = 'NavigationState';
NavigationDispatchContext.displayName = 'NavigationDispatch';
